import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable, InjectionToken, OnDestroy } from '@angular/core';
import { from, Observable, Subject, switchMap, takeUntil } from 'rxjs';
import { HeaderLoginService } from '../public-services';
import { OsApiAuthRealm, OsApiAuthRealms } from './os-api-auth.interface';
import { OsApiAuthService } from './os-api-auth.service';

export const API_AUTH_REALMS = new InjectionToken<OsApiAuthRealms>('OS_API:API_BASE_URL');

/**
 * Interceptor to handle authentication tokens/headers for certain HTTP requests
 */
@Injectable({
    providedIn: null,
})
export class OsApiAuthInterceptor implements HttpInterceptor, OnDestroy {
    private xCsrfToken = '';
    private destroySubject = new Subject<void>();

    public constructor(
        @Inject(API_AUTH_REALMS) private apiRealms: OsApiAuthRealms,
        private authService: OsApiAuthService,
        private loginService: HeaderLoginService
    ) {
        this.authService.xCsrfToken
            .pipe(takeUntil(this.destroySubject))
            .subscribe((token) => (this.xCsrfToken = token));
    }

    public ngOnDestroy(): void {
        this.destroySubject.next();
        this.destroySubject.complete();
    }

    public intercept<T>(req: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
        const apiRealm = this.apiRealms.find((realm) => req.url.startsWith(realm.path));
        if (!apiRealm) {
            return next.handle(req);
        }

        return this.realmIntercept(apiRealm, req, next);
    }

    private realmIntercept<T>(
        realm: OsApiAuthRealm,
        req: HttpRequest<T>,
        next: HttpHandler
    ): Observable<HttpEvent<T>> {
        const newRequestBase = {
            url: `${realm.apiUrl}${req.url.replace(realm.path, '')}`,
            withCredentials: realm.withCredentials,
            setHeaders: realm.headers,
        };

        const buildHandleWithToken = (accessToken: string | null): Observable<HttpEvent<T>> => {
            const isGet = req.method === 'GET';

            const headers = {
                ...newRequestBase.setHeaders,
                ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
                ...(!isGet && realm.useXCsrfToken && { 'x-csrf-token': this.xCsrfToken }),
            };

            const cloned = req.clone({
                ...newRequestBase,
                setHeaders: headers,
            });

            const baseHandle = next.handle(cloned);

            // Retrigger logic (skip generic basket workaround; handled by URL check like before)
            if (!realm.retriggerAfterLoginChange || newRequestBase.url.includes('/basket')) {
                return baseHandle;
            }

            const needsRetrigger = realm.withCredentials || (!isGet && realm.useXCsrfToken);

            return needsRetrigger ? this.loginService.loggedIn.pipe(switchMap(() => baseHandle)) : baseHandle;
        };

        if (realm.accessTokenApiName) {
            return from(this.authService.getAccesstokenForAPI(realm.accessTokenApiName)).pipe(
                switchMap((token) => buildHandleWithToken(token))
            );
        }

        return buildHandleWithToken(null);
    }
}
